import { Calendar03Icon, XIcon } from '@/icons';
import { DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import { memo, useCallback, useState } from 'react';

const { RangePicker } = DatePicker;

type Props = {
  startDate?: string;
  endDate?: string;
  onChange: (start: string, end: string) => void;
  onCalendarChange?: RangePickerProps['onCalendarChange'];
  disabledFuture?: boolean;
  disabledDate?: (current: Dayjs) => boolean;
  picker?: 'date' | 'month' | 'year' | 'quarter';
  format?: string;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  className?: string;
  onReset?: () => void;
  isChanged?: boolean;
  disabled?: boolean | [boolean, boolean];
};

const CustomRangePicker = ({
  startDate,
  endDate,
  onChange,
  onCalendarChange,
  disabledFuture = true,
  disabledDate,
  picker = 'date',
  format,
  placement = 'bottomRight',
  className,
  onReset,
  isChanged,
  disabled,
}: Props) => {
  const [open, setOpen] = useState(false);

  const getFormat = () => {
    if (format) return format;
    if (picker === 'month') return 'YYYY-MM';
    if (picker === 'year') return 'YYYY';
    return 'YYYY-MM-DD';
  };

  const handleChange: RangePickerProps['onChange'] = useCallback(
    (_: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
      onChange(dateStrings[0], dateStrings[1]);
      setOpen(false);
    },
    [onChange],
  );

  const defaultDisabledDate = (current: Dayjs) => {
    if (disabledDate) return disabledDate(current);
    if (disabledFuture) return current && current > dayjs().endOf('day');
    return false;
  };

  return (
    <div className={`relative ${className || ''}`}>
      {/* ICON BUTTON */}
      <div className="flex items-center gap-1 rounded border border-theme-gray-700 p-1.5 hover:border-theme-primary transition-colors cursor-pointer bg-theme-slate-800">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center text-white"
        >
          <Calendar03Icon size={22} />
        </button>

        {isChanged && onReset && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
            className="flex items-center text-theme-gray-400 hover:text-white transition-colors border-l border-theme-gray-700 pl-1 ml-0.5"
          >
            <XIcon size={16} />
          </button>
        )}
      </div>

      {/* HIDDEN RANGE PICKER */}
      <RangePicker
        open={open}
        picker={picker}
        format={getFormat()}
        value={[
          startDate ? dayjs(startDate) : null,
          endDate ? dayjs(endDate) : null,
        ]}
        onChange={handleChange}
        onCalendarChange={onCalendarChange}
        onOpenChange={setOpen}
        disabledDate={defaultDisabledDate}
        allowClear={false}
        suffixIcon={null}
        placement={placement}
        disabled={disabled}
        className="absolute inset-0 opacity-0 pointer-events-none"
      />
    </div>
  );
};

export default memo(CustomRangePicker);
